/**
 * Shared theme helpers for the studio card set (widget types 10-19).
 *
 * The dashboard twin of this file reads the same tokens from
 * CustomizeWallContext (famewall-React/src/components/cards/studio-cards/
 * studioTheme.js). Embeds have no context - every wall setting arrives as a
 * prop - so this is a plain function instead of a hook.
 *
 * KEEP THE TOKEN NAMES IDENTICAL to the dashboard file. The cards are ported
 * line for line between the two, and a renamed token is the easiest way for
 * the preview and the real widget to drift apart.
 */

/** Pinboard's default note colour. The widget picker resets Card Color to this. */
export const PINBOARD_PAPER = '#fff2a1';

export const STUDIO_SERIF = "Georgia, 'Times New Roman', Times, serif";
export const STUDIO_MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** #rgb / #rrggbb -> rgba(). Any other colour string is returned untouched. */
export const hexToRgba = (color, alpha = 1) => {
  if (!color || typeof color !== 'string') return `rgba(0,0,0,${alpha})`;
  const trimmed = color.trim();
  if (trimmed.charAt(0) !== '#') return trimmed;
  let hex = trimmed.slice(1);
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) return trimmed;
  const int = parseInt(hex, 16);
  return `rgba(${(int >> 16) & 255},${(int >> 8) & 255},${int & 255},${alpha})`;
};

/** Rough relative luminance, used to pick readable ink on a brand-coloured band. */
export const readableInk = (color, darkInk = '#15171c', lightInk = '#ffffff') => {
  if (!color || typeof color !== 'string' || color.charAt(0) !== '#') return lightInk;
  let hex = color.trim().slice(1);
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  if (hex.length !== 6 || /[^0-9a-f]/i.test(hex)) return lightInk;
  const int = parseInt(hex, 16);
  const channel = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminance =
    0.2126 * channel((int >> 16) & 255) +
    0.7152 * channel((int >> 8) & 255) +
    0.0722 * channel(int & 255);
  return luminance > 0.5 ? darkInk : lightInk;
};

/**
 * Resolves the wall props every embed card receives into the flat token set
 * the studio cards use. Returns the props too, so a card only needs this one
 * call.
 */
export const studioTheme = (props = {}) => {
  const dark = !!props.darkModeEnabled;
  const fontColor = props.cardFontColor || null;

  const ink = fontColor || (dark ? '#f2f3f7' : '#15171c');
  const muted = fontColor ? hexToRgba(fontColor, 0.66) : dark ? '#9aa1b0' : '#636a78';

  return {
    ...props,
    dark,
    ink,
    muted,
    line: dark ? 'rgba(255,255,255,0.14)' : '#e5e8ee',
    strongLine: dark ? 'rgba(255,255,255,0.28)' : '#c9cedb',
    soft: dark ? 'rgba(255,255,255,0.07)' : '#f4f5f8',
    surface: dark ? '#151821' : '#ffffff',
    /** star colour */
    accent: props.ratingColor || '#f59e0b',
    /** decorative brand colour - drives bands, blocks and quote marks */
    brand: props.quotationColor || '#7975f0',
    highlight: props.highlightColor || '#fcf807',
    link: props.linkColor || '#0b57a8',
    fontClass: props.fontStyle && props.fontStyle !== '' ? `font-${props.fontStyle}` : '',
    shadow: dark
      ? '0 14px 34px rgba(0,0,0,0.45)'
      : '0 10px 30px rgba(20,24,40,0.10), 0 1px 2px rgba(20,24,40,0.06)',
    /* the dashboard context calls this hideDate; embeds send isDateTimeDisabled */
    hideDate: !!props.isDateTimeDisabled,
    cardBackgroundObject: props.cardBackgroundObject || {},
  };
};

export default studioTheme;
